<template>
  <div
    class="voice-message"
    :class="{
      'is-playing': isPlaying,
      'is-loading': mediaLoading,
      'is-disabled': mediaError
    }"
    role="button"
    tabindex="0"
    @click="handleToggle"
    @keydown.enter.prevent="handleToggle"
    @keydown.space.prevent="handleToggle"
  >
    <div class="voice-icon" aria-hidden="true">
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          class="wave wave-1"
          d="M4.54714 6.1104L2.65662 7.99868L4.5449 9.88696C5.58872 8.84538 5.58872 7.15422 4.54714 6.1104ZM6.43542 11.7775L7.37844 12.7205C9.98799 10.1132 9.98799 5.88416 7.37844 3.27686L6.43542 4.22212C8.52306 6.30751 8.52306 9.69209 6.43542 11.7775Z"
          fill="currentColor"
          stroke="currentColor"
          stroke-width="0.35"
          stroke-linejoin="round"
        />
        <path
          class="wave wave-2"
          d="M9.26892 1.38882C12.92 5.03995 12.92 10.9601 9.26892 14.609L10.2119 15.5565C14.385 11.3835 14.385 4.6166 10.2119 0.445801L9.26892 1.38882Z"
          fill="currentColor"
          stroke="currentColor"
          stroke-width="0.35"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <span class="voice-duration">{{ durationLabel }}</span>
    <audio
      ref="audioRef"
      :src="audioUrl || undefined"
      preload="none"
      @ended="handleEnded"
      @pause="handlePause"
      @play="handlePlay"
      @error="handleAudioError"
    />
  </div>
</template>

<script setup>
import { computed, defineProps, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { formatVoiceDuration, resolveAuthMediaUrl } from './utils'

const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  playLength: {
    type: [Number, String],
    default: ''
  }
})

const audioRef = ref(null)
const audioUrl = ref('')
const isPlaying = ref(false)
const mediaLoading = ref(false)
const mediaError = ref(false)
const playWhenReady = ref(false)

const durationLabel = computed(() => formatVoiceDuration(props.playLength))

const revokeAudioUrl = () => {
  if (audioUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(audioUrl.value)
  }
}

const stopPlayback = () => {
  const audio = audioRef.value
  if (audio) {
    audio.pause()
    audio.currentTime = 0
  }
  isPlaying.value = false
}

const startPlay = async () => {
  await nextTick()
  const audio = audioRef.value
  if (!audio || !audioUrl.value) return false

  audio.currentTime = 0
  try {
    await audio.play()
    return true
  } catch (e) {
    console.error('语音播放失败:', e)
    isPlaying.value = false
    return false
  }
}

const loadAudio = async url => {
  mediaLoading.value = true
  mediaError.value = false
  stopPlayback()
  revokeAudioUrl()
  audioUrl.value = ''

  try {
    const resolved = await resolveAuthMediaUrl(url)
    if (!resolved) {
      mediaError.value = true
      playWhenReady.value = false
      return
    }
    audioUrl.value = resolved
    await nextTick()
    if (playWhenReady.value) {
      playWhenReady.value = false
      await startPlay()
    }
  } finally {
    mediaLoading.value = false
  }
}

watch(
  () => props.src,
  url => {
    playWhenReady.value = false
    loadAudio(url)
  },
  { immediate: true }
)

const handleToggle = async () => {
  if (mediaError.value) return

  if (mediaLoading.value || !audioUrl.value) {
    playWhenReady.value = true
    return
  }

  const audio = audioRef.value
  if (!audio) return

  if (isPlaying.value) {
    audio.pause()
    return
  }

  await startPlay()
}

const handlePlay = () => {
  isPlaying.value = true
}

const handlePause = () => {
  isPlaying.value = false
}

const handleEnded = () => {
  const audio = audioRef.value
  if (audio) {
    audio.currentTime = 0
  }
  isPlaying.value = false
}

const handleAudioError = () => {
  mediaError.value = true
  isPlaying.value = false
}

onBeforeUnmount(() => {
  playWhenReady.value = false
  stopPlayback()
  revokeAudioUrl()
})
</script>

<style scoped lang="scss">
.voice-message {
  width: max-content;
  display: inline-flex;
  align-items: center;
  gap: 11px;
  padding: 10px 16px;
  border-radius: 0 8px 8px 8px;
  background: #f2f2f2;
  cursor: pointer;
  user-select: none;
  color: #333;
  transition: background 0.2s ease;

  &:hover {
    background: #ebebeb;
  }

  &:focus-visible {
    outline: 2px solid #91caff;
    outline-offset: 2px;
  }

  &.is-loading {
    cursor: wait;
    opacity: 0.7;
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.voice-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  line-height: 0;

  svg {
    display: block;
    transform: scale(1.12);
    transform-origin: 20% 50%;
  }
}

.voice-duration {
  font-size: 16px;
  line-height: 22px;
  color: #333;
}

.voice-audio {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.wave {
  transform-origin: center;
}

.is-playing {
  .wave-1 {
    animation: voice-wave-1 1s ease-in-out infinite;
  }

  .wave-2 {
    animation: voice-wave-2 1s ease-in-out infinite;
  }
}

@keyframes voice-wave-1 {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.35;
    transform: scale(0.88);
  }
}

@keyframes voice-wave-2 {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.88);
  }

  50% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
